import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  FilterAmenities,
  FilterCancellation,
  FilterListSort,
  FilterRating,
  HotelNameFilter,
  HotelsRawEventName,
  MealPlanFilter,
  UserRating,
  useEmitCoreHotelsRawEvent,
} from "@tenant/hotels";
import {
  FilterDialogContainer,
  FilterDialogSection,
  FilterPrice,
} from "@tenant/ui";

import { usePackageHotelFilterDialog } from "./usePackageHotelFilterDialog";
import defaultStyles from "@/overrides/hotels/availability/components/FilterDialog/FilterDialog.module.scss";

type FilterDialogType = {
  filterType?: "all" | "homes" | "hotels";
};

/**
 * Package-specific filter dialog for hotels that uses package total pricing (hotel + flight + car).
 * This is swapped in via PackagesHotelSlotProvider for the packages flow only.
 */
export const PackageHotelFilterDialog = (props: FilterDialogType) => {
  const styles = useFlightStyles(
    CoreHotelsComponent.FilterDialog,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, CoreHotelsComponent.FilterDialog);

  const {
    text: { price: priceText, ...restText },
    context: {
      priceRange,
      amenities,
      cancellation,
      starRating,
      filterOptions,
      mealPlan,
      totalFilterCount,
      filtersChanged,
      hotelName,
      userRating,
      sortOption,
    },
    handlers: {
      updateValuesToMatchState,
      resetAllFilters,
      applyFilter,
      onPriceChange,
      onAmenitiesChange,
      onCancellationChange,
      onRatingChange,
      onMealPlanChange,
      onHotelNameChange,
      onUserRatingChange,
      onSortChange,
    },
  } = usePackageHotelFilterDialog();

  const { t } = useI18nContext();

  const emitFilterOpened = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelFilterOpened
  );

  const onOpen = () => {
    emitFilterOpened();
    updateValuesToMatchState();
  };

  return (
    <FilterDialogContainer
      onClickDialogTrigger={onOpen}
      onClickReset={resetAllFilters}
      onClickApply={applyFilter}
      className={block}
      buttonText={t("core-hotels.availabilityPage.filters.button")}
      numberOfFilters={totalFilterCount}
      filtersChanged={filtersChanged}
    >
      <FilterDialogSection
        className={cn("sort")}
        title={t("sortBy")}
        content={
          <FilterListSort selectedSort={sortOption} onChange={onSortChange} />
        }
      />
      <FilterDialogSection
        title={restText.rating.title}
        content={
          <FilterRating selectedValues={starRating} onChange={onRatingChange} />
        }
      />
      <FilterDialogSection
        title={restText.mealPlan.title}
        content={
          <MealPlanFilter
            selectedValues={mealPlan ?? []}
            onChange={onMealPlanChange}
          />
        }
      />
      <FilterDialogSection
        className={cn("amenities")}
        title={restText.amenities.title}
        content={
          <FilterAmenities
            options={filterOptions.amenities}
            selectedValues={amenities}
            onChange={onAmenitiesChange}
          />
        }
      />
      <FilterDialogSection
        className={cn("price")}
        title={priceText.title}
        content={
          <FilterPrice
            isMinValueConfigurable
            minimumPrice={filterOptions.priceRange?.min}
            maximumPrice={filterOptions.priceRange?.max}
            initialValue={{
              minPrice: priceRange.min,
              maxPrice: priceRange.max,
            }}
            onFilterPriceChange={onPriceChange}
            label={priceText.description}
          />
        }
      />
      <FilterDialogSection
        title={restText.cancellation.title}
        className={cn("cancellation")}
        content={
          <FilterCancellation
            isFreeCancellationSelected={cancellation}
            onChange={onCancellationChange}
          />
        }
      />
      <FilterDialogSection
        title={restText.hotelName.title}
        content={
          <HotelNameFilter
            selectedHotelName={hotelName ?? ""}
            onChange={onHotelNameChange}
            description={restText.hotelName.description}
            label={restText.hotelName.label}
          />
        }
      />
      <FilterDialogSection
        title={restText.userRating.title}
        content={
          <UserRating value={userRating} onChange={onUserRatingChange} />
        }
      />
    </FilterDialogContainer>
  );
};
